/**
 * Pure view model of the «Рассылки» screen (contract line #32, T.Z124).
 *
 * Framework-free on purpose: the `unit` Playwright project runs in plain Node,
 * where neither React nor `import.meta.env` exists — nothing here value-imports
 * the api layer (type-only imports are erased at runtime), the same rule
 * `../quotas/quotas-model` documents.
 *
 * The page's OWN logic is the owner's spec, restated where the user reads it:
 * the sending type is prefilled, the inclusion logic is BOTH buckets of the
 * «не подписали договор» register filters, and the letter points at the
 * welcome page of the child's programme track.
 */

import type {
  MailingsCapabilitiesDto,
  MailingsRunProgress,
  MailingsRunStatusDto,
  MailingConsentRunProgress,
  MailingConsentSummaryDto,
  MailingDeliveryFilter,
  MailingDeliveryStatus,
  MailingLinkRunProgress,
  MailingLinkSummaryDto,
  MailingMessageDto,
  MailingOutcomeFilter,
  MailingRecipientDto,
  MailingSummaryDto,
  MailingTrack,
} from '../../api/mailings';

/** The sending types the page offers (T.Z140 added the second, T.Z182 the third).
 * The values are the api's `mailing_type` labels, the select's option values. */
export const MAILING_TYPE_DOCUMENTS_UNSIGNED = 'DOCUMENTS_UNSIGNED' as const;
export const MAILING_TYPE_EVI_NOT_FINISHED = 'EVI_NOT_FINISHED' as const;
export const MAILING_TYPE_CONSENT_UNSIGNED = 'CONSENT_ERROR_UNSIGNED_14_18' as const;
export type MailingFixedType =
  | typeof MAILING_TYPE_DOCUMENTS_UNSIGNED
  | typeof MAILING_TYPE_EVI_NOT_FINISHED
  | typeof MAILING_TYPE_CONSENT_UNSIGNED;

/** The ONE sending type this page shipped before T.Z140 — still the default. */
export const MAILING_TYPE_LABEL = 'Рассылка не подписавшим договор';
/** The SECOND fixed type's label (T.Z140), the owner's own wording. */
export const MAILING_TYPE_LABEL_LINK = 'Рассылка получившим ссылку';
/** The THIRD fixed type's label (T.Z182), the owner's own name for the mailing —
 * the full wording, audience included. */
export const MAILING_TYPE_LABEL_CONSENT =
  'Ошибка подписи договора: не указано согласие родителей для заявителей от 14 до 18 лет';

/** The type labels, keyed by the wire value (the select's option list). */
export const MAILING_TYPE_LABELS: Record<MailingFixedType, string> = {
  [MAILING_TYPE_DOCUMENTS_UNSIGNED]: MAILING_TYPE_LABEL,
  [MAILING_TYPE_EVI_NOT_FINISHED]: MAILING_TYPE_LABEL_LINK,
  [MAILING_TYPE_CONSENT_UNSIGNED]: MAILING_TYPE_LABEL_CONSENT,
};

/** The inclusion logic the page states in RU, verbatim the owner's spec. */
export const LOGIC_DESCRIPTION =
  'Письмо получают родители детей, которые прошли ЕВИ, но ещё не подписали договор на обучение — ' +
  'оба списка: «Не перешли в iSmart» (ребёнок прошёл испытание, но не появился на платформе) ' +
  'и «Не подписали» (пришёл на платформу, но договор не подписал). ' +
  'В письме — ссылка и кнопка на приветственную страницу трека ребёнка: ' +
  'программы ИИ ведут на edu.future.ismart.org/start/ai/, программы Python — на edu.future.ismart.org/start/python/.';

/** The SECOND fixed type's inclusion logic (T.Z140), the owner's spec restated:
 * who is included, and what makes a letter unsendable. */
export const LOGIC_DESCRIPTION_LINK =
  'Письмо получают родители детей, которые получили ссылку на вступительное испытание (статус «Получил ссылку»), ' +
  'но ещё не прошли его. Ссылка в письме — своя для каждого ребёнка: страница ЕГО курса на Госуслугах ' +
  '(gosuslugi.ru/futurecode/<курс> — номер курса берётся из заявки самого ребёнка, без приписанных чисел), ' +
  'формат его курса задаёт вид страницы (офлайн → view=offline, онлайн → view=online), ' +
  'а аудитория берётся из программы и класса: 8–9 классы → aud=8_9, 10–11 классы → aud=10_11, СПО → aud=college. ' +
  'Ребёнок без e-mail, без разобранного имени или с неопределившейся ссылкой не получает письмо ' +
  'и остаётся в счётчике «не готовы к отправке».';

export const BUCKET_LABELS: Record<MailingRecipientDto['bucket'], string> = {
  NOT_ARRIVED: 'Не перешёл в iSmart',
  NOT_SIGNED: 'Не подписал',
};

/** The THIRD fixed type's inclusion logic (T.Z182), the owner's spec restated in
 * the same words the register's filters speak: the five axes, the 14–17 window,
 * and what makes a letter unsendable. */
export const LOGIC_DESCRIPTION_CONSENT =
  'Письмо получают родители детей от 14 до 17 лет включительно, чей договор уже подписан, но при подписании не было ' +
  'отмечено согласие законного представителя — ребёнок сам подписал (совпадение подписанта подтверждено) и он ещё ' +
  'не зачислен. В письме — просьба пройти подписание повторно и обязательно отметить пункт ' +
  '«Подписываю договор с согласия законного представителя (родителя)». Ссылки в письме (T.Z185, две отдельные): ' +
  '«личный кабинет» — приветственная страница трека ребёнка, программы ИИ — edu.future.ismart.org/start/ai/, ' +
  'программы Python — edu.future.ismart.org/start/python/; «Перейти на страницу курса» — его страница курса ' +
  'на Госуслугах: его курс, его формат (офлайн/онлайн) и его аудитория. Ребёнок без e-mail, без разобранного имени или без ' +
  'определившейся страницы курса письмо не получает и остаётся в счётчике «не готовы к отправке».';

export const TRACK_LABELS: Record<MailingTrack, string> = {
  AI: 'ИИ',
  PYTHON: 'Python',
};

/** The audience segments the second type's link carries (T.Z140) — the RU wording
 * of the owner's three query tokens. */
export const AUDIENCE_LABELS: Record<string, string> = {
  '8_9': '8–9 классы',
  '10_11': '10–11 классы',
  college: 'СПО',
};

export const TRACK_URLS: Record<MailingTrack, string> = {
  AI: 'https://edu.future.ismart.org/start/ai/',
  PYTHON: 'https://edu.future.ismart.org/start/python/',
};

export const DELIVERY_STATUS_LABELS: Record<MailingDeliveryStatus, string> = {
  ACCEPTED: 'Принято',
  DELIVERED: 'Доставлено',
  FAILED: 'Ошибка',
  SKIPPED: 'Пропущено',
};

/** The four delivery chips — value, RU label, and the summary counter it reads. */
export interface DeliveryFilterOption {
  value: MailingDeliveryFilter | '';
  label: string;
  countKey: keyof MailingSummaryDto['delivery'];
}

export const DELIVERY_FILTERS: readonly DeliveryFilterOption[] = [
  { value: '', label: 'Все', countKey: 'accepted' },
  { value: 'not_sent', label: 'Не получил письмо', countKey: 'notSent' },
  { value: 'accepted', label: 'Принято', countKey: 'accepted' },
  { value: 'delivered', label: 'Доставлено', countKey: 'delivered' },
  { value: 'failed', label: 'Ошибка', countKey: 'failed' },
];

export function deliveryCount(summary: MailingSummaryDto | undefined, filter: MailingDeliveryFilter | ''): number | null {
  if (!summary) return null;
  const option = DELIVERY_FILTERS.find((candidate) => candidate.value === filter);
  if (!option) return null;
  return summary.delivery[option.countKey];
}

// ------------------------------------------------- post-send outcomes (T.Z146) --

/** The cards' wording (T.Z148 renamed the first: «после отправки письма» says the
 * measurement is TIME-based — the letter's send moment — and claims nothing about
 * the click that may or may not have caused the signature). */
export const OUTCOME_LABELS: Record<MailingOutcomeFilter, string> = {
  'signed-after': 'Подписали после отправки письма',
  'declined-after': 'Отказались после рассылки',
};

/** T.Z148 — the honest definition each number carries as its tooltip: what is
 * counted, over which universe, and what the number does NOT claim (attribution —
 * Postbox click data lives outside the system and is not imported). */
export const OUTCOME_HINTS: Record<MailingOutcomeFilter, string> = {
  'signed-after':
    'Договор подписан после отправки письма этому ребёнку — счёт по времени, а не по кликам: клики из письма в систему не поступают.',
  'declined-after':
    'Ребёнок ушёл в «Отказ» после отправки письма — тоже счёт по времени, без утверждения, что причиной было письмо.',
};

/** T.Z148 — «всего получателей» is a LIVING snapshot, not the send-time roster:
 * the two buckets are re-derived on every read, so children who signed or were
 * declined since the send have left it and children who fell into the buckets
 * afterwards have joined it. It is therefore never equal to the number of letters
 * the campaign actually sent (the journal's own count). */
export const TOTAL_RECIPIENTS_HINT =
  'Живой снимок «сейчас»: не подписали договор и не отказались. После отправки состав меняется — ' +
  'подписавшие и отказавшиеся уходят, новые дети приходят, поэтому число не равно числу отправленных писем.';

/** The outcome select — «Все / Подписали после / Отказались после». The counts
 * read `summary.outcomes`, the same fold the api's filter narrows by. */
export interface OutcomeFilterOption {
  value: MailingOutcomeFilter | '';
  label: string;
}

export const OUTCOME_FILTERS: readonly OutcomeFilterOption[] = [
  { value: '', label: 'Все' },
  { value: 'signed-after', label: OUTCOME_LABELS['signed-after'] },
  { value: 'declined-after', label: OUTCOME_LABELS['declined-after'] },
];

/** The outcome counts a summary without the ADDITIVE v1.9.39 block renders —
 * zeros, never a NaN card. */
export const EMPTY_OUTCOMES: NonNullable<MailingSummaryDto['outcomes']> = {
  signedAfterSend: 0,
  declinedAfterSend: 0,
};

/** The filter literal → the summary counter it reads: the wire speaks
 * `signed-after`/`declined-after` (the `?delivery=` grammar), the summary block
 * speaks camelCase — ONE mapping so a card and its filter can never diverge. */
export const OUTCOME_COUNT_KEYS: Record<MailingOutcomeFilter, keyof NonNullable<MailingSummaryDto['outcomes']>> = {
  'signed-after': 'signedAfterSend',
  'declined-after': 'declinedAfterSend',
};

/** The counts the two cards render — the answer's own block, or zeros. */
export function outcomeCounts(summary: MailingSummaryDto | undefined): NonNullable<MailingSummaryDto['outcomes']> {
  return summary?.outcomes ?? EMPTY_OUTCOMES;
}

/** The number behind ONE outcome filter value (the select's option label). */
export function outcomeCount(
  summary: MailingSummaryDto | undefined,
  value: MailingOutcomeFilter,
): number {
  return outcomeCounts(summary)[OUTCOME_COUNT_KEYS[value]];
}

// ------------------------------------------------------------- URL params --

export const PARAM = {
  BUCKET: 'bucket',
  DELIVERY: 'delivery',
  OUTCOME: 'outcome',
  PAGE: 'page',
  TYPE: 'type',
} as const;

/** The `?type=` of the classic tab: which fixed mailing the panel renders.
 * Absent/junk = the first type (the tab's own default since T.Z124); the second
 * (T.Z140) and the third (T.Z182) are the only accepted changes. */
export function parseTypeParam(value: string | null): MailingFixedType {
  if (value === MAILING_TYPE_EVI_NOT_FINISHED) return MAILING_TYPE_EVI_NOT_FINISHED;
  if (value === MAILING_TYPE_CONSENT_UNSIGNED) return MAILING_TYPE_CONSENT_UNSIGNED;
  return MAILING_TYPE_DOCUMENTS_UNSIGNED;
}

export function parseBucketParam(value: string | null): MailingRecipientDto['bucket'] | undefined {
  return value === 'NOT_ARRIVED' || value === 'NOT_SIGNED' ? value : undefined;
}

export function parseDeliveryParam(value: string | null): MailingDeliveryFilter | undefined {
  return value === 'not_sent' || value === 'accepted' || value === 'delivered' || value === 'failed' ? value : undefined;
}

/** T.Z146 — the `?outcome=` of the classic panel: junk reads as «all» (the api
 * still 400s a junk literal; the page simply does not send one). */
export function parseOutcomeParam(value: string | null): MailingOutcomeFilter | undefined {
  return value === 'signed-after' || value === 'declined-after' ? value : undefined;
}

export function parsePageParam(value: string | null): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
}

// -------------------------------------------------------------- history ----

/** One line of the recipient's history block — the ledger row in RU. The type
 * label follows the ROW's own `mailingType` (T.Z140: a history can mix the fixed
 * types' letters once more than one has run), so a row is never mislabelled. */
export function formatHistoryLine(message: MailingMessageDto): string {
  const when = message.sentAt ? new Date(message.sentAt).toLocaleString('ru-RU') : '—';
  const status = DELIVERY_STATUS_LABELS[message.deliveryStatus];
  const track = TRACK_LABELS[message.track];
  const typeLabel =
    message.mailingType === MAILING_TYPE_EVI_NOT_FINISHED
      ? MAILING_TYPE_LABEL_LINK
      : message.mailingType === MAILING_TYPE_CONSENT_UNSIGNED
        ? MAILING_TYPE_LABEL_CONSENT
        : MAILING_TYPE_LABEL;
  const base = `${when} · ${typeLabel} · ${status} · ${track}`;
  return message.error ? `${base} — ${message.error}` : base;
}

/** The «когда и в каком статусе отправляли» line the owner asked the ledger to
 * answer. Structural on purpose — BOTH fixed types' recipient rows (T.Z140) and
 * the list rows fold through the same three ledger fields. */
export function formatLastSend(recipient: {
  lastSentAt: string | null;
  lastDeliveryStatus: MailingDeliveryStatus | null;
  lastStatusAtSend: string | null;
}): string {
  if (!recipient.lastSentAt) return '—';
  const when = new Date(recipient.lastSentAt).toLocaleString('ru-RU');
  const status = recipient.lastDeliveryStatus ? DELIVERY_STATUS_LABELS[recipient.lastDeliveryStatus] : '';
  const funnel = recipient.lastStatusAtSend ? ` · статус: ${recipient.lastStatusAtSend}` : '';
  return `${when}${status ? ` · ${status}` : ''}${funnel}`;
}

// ---------------------------------------------------------- run progress ---

/** The «Отправить всем» progress line — counts only, no addresses. */
export function formatRunProgress(run: MailingsRunProgress): string {
  const scope = run.bucket === 'ALL' ? 'оба списка' : BUCKET_LABELS[run.bucket];
  return `Отправлено ${run.processed} из ${run.total} (${scope}) — принято ${run.accepted}, ошибок ${run.failed}, пропущено ${run.skipped}`;
}

/** The second type's progress line — no bucket scope to name (T.Z140). */
export function formatLinkRunProgress(run: MailingLinkRunProgress): string {
  return `Отправлено ${run.processed} из ${run.total} — принято ${run.accepted}, ошибок ${run.failed}, пропущено ${run.skipped}`;
}

/** The third type's progress line (T.Z182) — the same counts, no bucket scope. */
export function formatConsentRunProgress(run: MailingConsentRunProgress): string {
  return `Отправлено ${run.processed} из ${run.total} — принято ${run.accepted}, ошибок ${run.failed}, пропущено ${run.skipped}`;
}

export function isRunRunning(status: MailingsRunStatusDto | undefined): boolean {
  return Boolean(status?.running);
}

// -------------------------------------------------------- capabilities -----

/**
 * T.Z134 — what a FAILED identity/set probe says. The api forwards the raw AWS
 * SDK error text in `error`, and on a send-only key that text is a JSON-parse
 * garbage line («SyntaxError: Unexpected token 'F', "Forbidden "… is not valid
 * JSON») — owner-reported from prod. The probe is a diagnostic, not a gate: the
 * send is proven to work without it, so the line says exactly that and never
 * repeats the SDK's words.
 */
export const CAPABILITIES_PROBE_UNAVAILABLE = 'проверка недоступна для ключа отправки — не блокирует отправку';

/** The honest config line: what the transport can and cannot do HERE. */
export function formatCapabilities(capabilities: MailingsCapabilitiesDto | undefined): string {
  if (!capabilities) return 'Проверяем транспорт…';
  if (!capabilities.configured) {
    return 'Отправка не настроена на этом контуре (нет учётных данных Postbox) — письма не уйдут.';
  }
  const identities = capabilities.identities.ok
    ? `идентичности SES: ${capabilities.identities.names.length}`
    : `идентичности SES: ${CAPABILITIES_PROBE_UNAVAILABLE}`;
  const sets = capabilities.configurationSets.ok
    ? `конфигурации SES: ${capabilities.configurationSets.names.length}`
    : `конфигурации SES: ${CAPABILITIES_PROBE_UNAVAILABLE}`;
  return `Отправитель ${capabilities.fromEmail} · ${identities} · ${sets} · темп ${capabilities.sendPerSecond}/с`;
}

/** The zero summary a loading page renders its chips from. */
export const EMPTY_SUMMARY: MailingSummaryDto = {
  buckets: { notArrived: 0, notSigned: 0, total: 0 },
  delivery: { notSent: 0, accepted: 0, delivered: 0, failed: 0 },
  tracks: { ai: 0, python: 0, unresolved: 0 },
  outcomes: { signedAfterSend: 0, declinedAfterSend: 0 },
};

/** The zero summary of the SECOND fixed type (no buckets — readiness instead). */
export const EMPTY_LINK_SUMMARY: MailingLinkSummaryDto = {
  total: 0,
  ready: 0,
  notReady: 0,
  delivery: { notSent: 0, accepted: 0, delivered: 0, failed: 0 },
  tracks: { ai: 0, python: 0, unresolved: 0 },
};

/** The zero summary of the THIRD fixed type (T.Z182) — the second type's shape. */
export const EMPTY_CONSENT_SUMMARY: MailingConsentSummaryDto = {
  total: 0,
  ready: 0,
  notReady: 0,
  delivery: { notSent: 0, accepted: 0, delivered: 0, failed: 0 },
  tracks: { ai: 0, python: 0, unresolved: 0 },
};
